import type { ReactNode } from "react";

export type CortexMenuScreen = "main" | "singleplayer" | "multiplayer" | "host" | "join" | "load-map" | "options" | "lobby" | "pause";

interface CortexMainMenuProps {
  screen: CortexMenuScreen;
  sessionActive: boolean;
  onNavigate: (screen: CortexMenuScreen) => void;
  onResume?: () => void;
  onReturnToMenu?: () => void;
  isFadingOut?: boolean;
  children?: ReactNode;
}

const titles: Record<CortexMenuScreen, { title: string; subtitle: string }> = {
  main: { title: "Cortex", subtitle: "Combat sandbox uplink. Select a deployment and stand by for live insertion." },
  singleplayer: { title: "Singleplayer", subtitle: "Choose the arena and mission package, then deploy." },
  multiplayer: { title: "Multiplayer", subtitle: "Host a session or link to an active Cortex relay." },
  host: { title: "Host Match", subtitle: "Bring a lobby online and stage the arena." },
  join: { title: "Join Match", subtitle: "Scan live lobbies and connect to a host." },
  "load-map": { title: "Load Map", subtitle: "Browse registered arenas and launch a sandbox run." },
  options: { title: "Options", subtitle: "Tune controls, video, and audio mix." },
  lobby: { title: "Ready Room", subtitle: "Pilots are staged and waiting for the match to go live." },
  pause: { title: "Paused", subtitle: "Session suspended. Resume when ready." },
};

const menuButtonClass = "group inline-flex w-full items-center justify-between gap-4 border-b border-white/10 py-3 text-left font-mono text-sm uppercase tracking-[0.34em] text-white/88 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]";
const actionClass = "font-mono text-[11px] uppercase tracking-[0.32em] text-white/60 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]";

function MainMenuButtons({ screen, sessionActive, onNavigate, onResume, onReturnToMenu }: Omit<CortexMainMenuProps, "children" | "isFadingOut">) {
  if (screen === "pause") {
    return (
      <nav className="mt-10 flex w-full max-w-md flex-col" aria-label="Pause menu">
        {onResume ? (
          <button type="button" className={menuButtonClass} onClick={onResume}>
            <span>Resume</span>
            <span className="text-[10px] tracking-[0.28em] text-[#ffb347]/80">ENTER</span>
          </button>
        ) : null}
        <button type="button" className={menuButtonClass} onClick={() => onNavigate("options")}>
          <span>Options</span>
          <span className="text-[10px] tracking-[0.28em] text-white/40">CONFIG</span>
        </button>
        {onReturnToMenu ? (
          <button type="button" className={menuButtonClass} onClick={onReturnToMenu}>
            <span>Return to Menu</span>
            <span className="text-[10px] tracking-[0.28em] text-white/40">ESC</span>
          </button>
        ) : null}
      </nav>
    );
  }

  if (screen !== "main") {
    return null;
  }

  const items = [
    { screen: "singleplayer" as const, label: "Singleplayer", hint: "SOLO" },
    { screen: "multiplayer" as const, label: "Multiplayer", hint: "ONLINE" },
    { screen: "load-map" as const, label: "Load Map", hint: "BROWSE" },
    { screen: "options" as const, label: "Options", hint: "CONFIG" },
  ];

  return (
    <nav className="mt-10 flex w-full max-w-md flex-col" aria-label="Main menu">
      {items.map((item) => (
        <button key={item.screen} type="button" className={menuButtonClass} onClick={() => onNavigate(item.screen)}>
          <span>{item.label}</span>
          <span className="text-[10px] tracking-[0.28em] text-white/40 transition-colors duration-200 group-hover:text-[#ffb347]/80 group-focus-visible:text-[#ffb347]/80">{item.hint}</span>
        </button>
      ))}
      {sessionActive && onReturnToMenu ? (
        <button type="button" className={menuButtonClass} onClick={onReturnToMenu}>
          <span>Return to Menu</span>
          <span className="text-[10px] tracking-[0.28em] text-white/40">QUIT</span>
        </button>
      ) : null}
    </nav>
  );
}

export function CortexMainMenu({ screen, sessionActive, onNavigate, onResume, onReturnToMenu, isFadingOut = false, children }: CortexMainMenuProps) {
  const copy = titles[screen];
  const backTarget = sessionActive ? "pause" : "main";

  return (
    <div
      className={`absolute inset-0 z-40 overflow-auto bg-[radial-gradient(circle_at_top_left,rgba(30,18,4,0.26),rgba(0,0,0,0.76)_38%,rgba(0,0,0,0.92)_100%)] px-6 py-8 text-white transition-opacity duration-[400ms] ${isFadingOut ? "pointer-events-none opacity-0" : "opacity-100"}`}
      style={{ animation: isFadingOut ? undefined : "cortex-menu-fade-in 400ms ease-out" }}
    >
      <style>{"@keyframes cortex-menu-fade-in{from{opacity:0}to{opacity:1}}"}</style>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_52%,rgba(0,0,0,0.14)_78%,rgba(0,0,0,0.45)_100%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(transparent 50%, rgba(255,255,255,0.08) 50%)", backgroundSize: "100% 4px" }} />

      <div className="relative flex min-h-full items-start">
        <div className="w-full max-w-4xl pt-2 md:pl-4 lg:pl-8">
          <div className="mb-10 flex flex-wrap items-start justify-between gap-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-8">
              <img
                src="/os/assets/icons/cortex-shooter.svg"
                alt="Cortex logo"
                className="h-36 w-36 shrink-0 drop-shadow-[0_0_30px_rgba(80,230,255,0.2)] md:h-44 md:w-44 lg:h-56 lg:w-56"
              />
              <div className="pt-2 md:pt-4">
                <h1 className="font-serif text-5xl font-semibold tracking-[0.02em] text-white md:text-6xl">{copy.title}</h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-1">
              {screen !== "main" && screen !== "pause" ? (
                <button type="button" className={actionClass} onClick={() => onNavigate(backTarget)}>
                  Back
                </button>
              ) : null}
              {sessionActive && screen !== "pause" && onResume ? (
                <button type="button" className={actionClass} onClick={onResume}>
                  Resume
                </button>
              ) : null}
              {sessionActive && onReturnToMenu ? (
                <button type="button" className={actionClass} onClick={onReturnToMenu}>
                  Return to Menu
                </button>
              ) : null}
            </div>
          </div>

          {screen === "main" || screen === "pause" ? (
            <MainMenuButtons screen={screen} sessionActive={sessionActive} onNavigate={onNavigate} onResume={onResume} onReturnToMenu={onReturnToMenu} />
          ) : (
            <div className="max-w-5xl border-t border-white/10 pt-8">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}